import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { SITE } from "../../lib/site";
import { notifications } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { readableNotifications } from "../../utils/notifications";

/**
 * The top bar: brand, notifications and the account menu. CS-2 (owner D-1, 2026-09-27): the MAIN navigation moved
 * to the Sidebar (desktop/tablet) and the bottom bar (phone) — business/20 §44, business/19 §6.
 *
 * The unread badge is polled every 60s — there is no realtime transport anywhere in this stack.
 *
 * PART 6 (2026-10-07; owner Part 6 notifications): the badge counts only unread notifications that HAVE a message.
 * Message rendering is Module 12 Batch 17 (not built), so a notification can exist with no content; counting it would
 * promise a message the Notifications page cannot show. It reads the SAME list the Notifications page reads (one query
 * key, one cache) — no new endpoint. When Module 12 renders content, the badge counts it with no change here.
 */
export default function Header() {
  const { user, signOut } = useAuth();

  const { data } = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => notifications.list({ limit: 50 }),
    refetchInterval: 60 * 1000,
    enabled: Boolean(user),
  });

  const unread = readableNotifications(data).filter((n) => !n.readAt).length;

  return (
    <header className="vp-header sticky-top">
      <div className="d-flex align-items-center gap-3 h-100 px-3 px-md-4">
        <Link to="/" className="vp-brand text-decoration-none vp-touch">
          <i className="bi bi-buildings" />
          <span className="d-none d-sm-inline">{SITE.shortName}</span>
        </Link>

        <div className="ms-auto d-flex align-items-center gap-2">
          <Link to="/notifications" className="btn btn-sm btn-light position-relative vp-touch vp-touch-square" aria-label="Notifications">
            <i className="bi bi-bell" />
            {unread > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill text-bg-danger">
                {unread > 99 ? "99+" : unread}
                <span className="visually-hidden">unread notifications</span>
              </span>
            )}
          </Link>

          <div className="dropdown">
            <button className="btn btn-sm btn-light dropdown-toggle vp-touch" data-bs-toggle="dropdown" aria-label="Account menu">
              <i className="bi bi-person-circle" />
              <span className="d-none d-md-inline ms-1">{user?.fullName?.split(" ")[0]}</span>
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li className="dropdown-header">
                <div className="fw-semibold">{user?.fullName}</div>
                {/* R&S Final Closure — the sign-in identity: the login email, or the login mobile for a mobile login. */}
                <div className="small text-muted">{user?.email || user?.loginMobile}</div>
              </li>
              <li><hr className="dropdown-divider" /></li>
              <li>
                <Link className="dropdown-item" to="/profile">
                  <i className="bi bi-person me-2" />Profile
                </Link>
              </li>
              <li>
                <Link className="dropdown-item" to="/security">
                  <i className="bi bi-shield-lock me-2" />Security
                </Link>
              </li>
              <li>
                <button className="dropdown-item" onClick={signOut}>
                  <i className="bi bi-box-arrow-right me-2" />Sign out
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
}
